// useCraftReview.js — WIRE-3: React surface for the Discovery-track human review
// queue (v2 Evaluator Layer). Thin wrapper over `apiFetch`; mutations are named
// exports so non-React callers/tests can reuse them. THE LAW: approve/reject is
// the only way an item moves — the UI never auto-applies anything.

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../lib/api.js';

/** The human-review queue for a status (default pending). */
export async function fetchCraftReviewQueue(status = 'pending') {
  return apiFetch(`/api/evaluator/review-queue?status=${encodeURIComponent(status)}`);
}

/** Approve/reject one review item. On approval the Worker returns the gated yaml. */
export async function setCraftReviewStatus(id, status) {
  return apiFetch(`/api/evaluator/review-queue/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: { status },
  });
}

/** The craft scores of one finished run. */
export async function fetchCraftVerdicts(runId) {
  return apiFetch(`/api/evaluator/craft-verdicts/${encodeURIComponent(runId)}`);
}

/** Poll the pending craft review queue. Returns { data, loading, error, refetch }. */
export function useCraftReviewQueue(status = 'pending', { pollIntervalMs = 0 } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runFetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await fetchCraftReviewQueue(status);
    setLoading(false);
    if (!res.ok) {
      setError(res.error || 'Failed to load review queue');
      return;
    }
    setData(res.data);
  }, [status]);

  useEffect(() => {
    runFetch();
  }, [runFetch]);

  useEffect(() => {
    if (!pollIntervalMs || pollIntervalMs <= 0) return undefined;
    const timer = setInterval(runFetch, pollIntervalMs);
    return () => clearInterval(timer);
  }, [pollIntervalMs, runFetch]);

  return { data, loading, error, refetch: runFetch };
}
